import { useState, useEffect } from 'react';
import {
  subscribeToStats,
  getHeadToHeadStats
} from '../services/statsService';
import { getFriends } from '../services/friendService';
import { safeString } from '../utils/safeString';
import '../styles/Scoreboard.css';

const Scoreboard = ({ userId }) => {
  const [overallStats, setOverallStats] = useState(null);
  const [gameStats, setGameStats] = useState({});
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [headToHeadStats, setHeadToHeadStats] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overall');
  const [h2hLoading, setH2hLoading] = useState(false);

  // Game types and display information
  const gameTypes = [
    { id: 'tictactoe', name: 'Tic Tac Toe', icon: '❌⭕' },
    { id: 'rps', name: 'Rock Paper Scissors', icon: '✊✋✌️' },
    { id: 'connect4', name: 'Connect 4', icon: '🔴🟡' },
    { id: 'dotsandboxes', name: 'Dots & Boxes', icon: '📦' },
    { id: 'americancheckers', name: 'American Checkers', icon: '🔴⚫' },
    { id: 'russiancheckers', name: 'Russian Checkers', icon: '⚫⚪' },
    { id: 'checkers', name: 'American Checkers', icon: '🔴⚫' }, // Legacy support
    { id: 'battleship', name: 'Battleship', icon: '⚓🎯' },
    { id: 'chess', name: 'Chess', icon: '♟️👑' }
  ];

  // Subscribe to real-time stats updates
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToStats(userId, (stats) => {
      setOverallStats(stats.overall);
      setGameStats(stats.byGame);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Load friends for head-to-head dropdown
  useEffect(() => {
    const loadFriends = async () => {
      try {
        const friendList = await getFriends(userId);
        setFriends(friendList);
      } catch (error) {
        console.error('Error loading friends:', error);
      }
    };

    if (userId) {
      loadFriends();
    }
  }, [userId]);

  // Load head-to-head stats when friend is selected
  useEffect(() => {
    if (!selectedFriend) {
      setHeadToHeadStats([]);
      return;
    }

    const loadHeadToHead = async () => {
      setH2hLoading(true);
      try {
        const h2h = await getHeadToHeadStats(userId, selectedFriend.userId);
        setHeadToHeadStats(h2h);
      } catch (error) {
        console.error('Error loading head-to-head stats:', error);
      } finally {
        setH2hLoading(false);
      }
    };

    loadHeadToHead();
  }, [userId, selectedFriend]);

  // Calculate overall H2H stats
  const getOverallH2H = () => {
    if (headToHeadStats.length === 0) return null;

    const totals = headToHeadStats.reduce(
      (acc, game) => ({
        wins: acc.wins + game.wins,
        losses: acc.losses + game.losses,
        draws: acc.draws + game.draws,
        totalGames: acc.totalGames + game.totalGames
      }),
      { wins: 0, losses: 0, draws: 0, totalGames: 0 }
    );

    return totals;
  };

  // Render loading state
  if (loading) {
    return (
      <div className="user-stats-board">
        <div className="user-stats-board-header">
          <h3>SCOREBOARD</h3>
        </div>
        <div className="user-stats-board-loading">
          <div className="loading-spinner"></div>
          <p>Loading stats...</p>
        </div>
      </div>
    );
  }

  // Render overall stats tab
  const renderOverallStats = () => {
    if (!overallStats || overallStats.totalGames === 0) {
      return (
        <div className="empty-state">
          <p className="empty-icon">🎮</p>
          <p className="empty-title">No games played yet</p>
          <p className="empty-message">Start playing to see your stats!</p>
        </div>
      );
    }

    return (
      <div className="overall-stats">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🎮</div>
            <div className="stat-value">{overallStats.totalGames}</div>
            <div className="stat-label">Total Games</div>
          </div>

          <div className="stat-card stat-wins">
            <div className="stat-icon">🏆</div>
            <div className="stat-value">{overallStats.totalWins}</div>
            <div className="stat-label">Wins</div>
          </div>

          <div className="stat-card stat-losses">
            <div className="stat-icon">😞</div>
            <div className="stat-value">{overallStats.totalLosses}</div>
            <div className="stat-label">Losses</div>
          </div>

          <div className="stat-card stat-draws">
            <div className="stat-icon">🤝</div>
            <div className="stat-value">{overallStats.totalDraws}</div>
            <div className="stat-label">Draws</div>
          </div>
        </div>

        <div className="win-rate-section">
          <div className="win-rate-label">
            <span>Win Rate</span>
            <span className="win-rate-percentage">{overallStats.winRate}%</span>
          </div>
          <div className="win-rate-bar">
            <div
              className="win-rate-fill"
              style={{ width: `${overallStats.winRate}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  // Render per-game stats tab
  const renderGameStats = () => {
    const hasAnyGames = Object.values(gameStats).some(stats => stats.gamesPlayed > 0);

    if (!hasAnyGames) {
      return (
        <div className="empty-state">
          <p className="empty-icon">🎮</p>
          <p className="empty-title">No games played yet</p>
          <p className="empty-message">Start playing to see your stats by game!</p>
        </div>
      );
    }

    return (
      <div className="game-stats">
        <div className="game-stats-grid">
          {gameTypes.filter(game => game.id !== 'checkers').map((game) => {
            const stats = gameStats[game.id] || {
              wins: 0,
              losses: 0,
              draws: 0,
              gamesPlayed: 0,
              winRate: 0
            };

            return (
              <div
                key={game.id}
                className={`game-stat-card ${stats.gamesPlayed === 0 ? 'no-games' : ''}`}
              >
                <div className="game-stat-header">
                  <div className="game-stat-icon">{game.icon}</div>
                </div>
                <div className="game-stat-name">{game.name}</div>

                {stats.gamesPlayed > 0 ? (
                  <>
                    <div className="game-stat-games">{stats.gamesPlayed} Games</div>
                    <div className="game-stat-record">
                      <span className="wins">🏆 {stats.wins}</span>
                      <span className="separator">-</span>
                      <span className="losses">❌ {stats.losses}</span>
                      <span className="separator">-</span>
                      <span className="draws">🤝 {stats.draws}</span>
                    </div>
                    <div className="game-stat-winrate">{stats.winRate}% Win Rate</div>
                  </>
                ) : (
                  <div className="game-stat-empty">No games played</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render head-to-head tab
  const renderHeadToHead = () => {
    if (friends.length === 0) {
      return (
        <div className="empty-state">
          <p className="empty-icon">👥</p>
          <p className="empty-title">No friends yet</p>
          <p className="empty-message">Add friends to compare your records!</p>
        </div>
      );
    }

    const overallH2H = getOverallH2H();

    return (
      <div className="head-to-head">
        <div className="friend-selector-wrapper">
          <label htmlFor="friend-select" className="friend-selector-label">
            Select a friend to compare:
          </label>
          <select
            id="friend-select"
            className="friend-selector"
            value={selectedFriend?.userId || ''}
            onChange={(e) => {
              const friend = friends.find(f => f.userId === e.target.value);
              setSelectedFriend(friend || null);
            }}
          >
            <option value="">Choose a friend...</option>
            {friends.map((friend) => (
              <option key={friend.userId} value={friend.userId}>
                {safeString(friend.displayName, 'Friend')}
              </option>
            ))}
          </select>
        </div>

        {selectedFriend && (
          <div className="h2h-display">
            {h2hLoading ? (
              <div className="h2h-loading">
                <div className="loading-spinner"></div>
                <p>Loading head-to-head stats...</p>
              </div>
            ) : headToHeadStats.length === 0 ? (
              <div className="h2h-empty">
                <p className="h2h-empty-icon">🎮</p>
                <p className="h2h-empty-text">
                  You haven't played any games with <strong>{safeString(selectedFriend?.displayName, 'this friend')}</strong> yet.
                </p>
                <p className="h2h-empty-hint">Invite them to play!</p>
              </div>
            ) : (
              <>
                <div className="h2h-title">
                  vs {safeString(selectedFriend?.displayName, 'Friend')}
                </div>

                <div className="h2h-games">
                  {headToHeadStats.map((game) => {
                    const gameInfo = gameTypes.find(g => g.id === game.gameType);
                    return (
                      <div key={game.gameType} className="h2h-game-row">
                        <div className="h2h-game-name">
                          <span className="h2h-game-icon">{gameInfo?.icon}</span>
                          <span>{gameInfo?.name || game.gameType}</span>
                        </div>
                        <div className="h2h-game-record">
                          <span className="h2h-wins">🏆 {game.wins}</span>
                          <span className="h2h-separator">-</span>
                          <span className="h2h-losses">❌ {game.losses}</span>
                          <span className="h2h-separator">-</span>
                          <span className="h2h-draws">🤝 {game.draws}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {overallH2H && (
                  <div className="h2h-overall">
                    <div className="h2h-overall-label">Overall Record</div>
                    <div className="h2h-overall-record">
                      <span className="h2h-wins">🏆 {overallH2H.wins}</span>
                      <span className="h2h-separator">-</span>
                      <span className="h2h-losses">❌ {overallH2H.losses}</span>
                      <span className="h2h-separator">-</span>
                      <span className="h2h-draws">🤝 {overallH2H.draws}</span>
                      <span className="h2h-total">({overallH2H.totalGames} games)</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-stats-board">
      <div className="user-stats-board-header">
        <h3>SCOREBOARD</h3>
      </div>

      <div className="user-stats-board-tabs">
        <button
          className={`user-stats-board-tab ${activeTab === 'overall' ? 'active' : ''}`}
          onClick={() => setActiveTab('overall')}
        >
          Overall
        </button>
        <button
          className={`user-stats-board-tab ${activeTab === 'pergame' ? 'active' : ''}`}
          onClick={() => setActiveTab('pergame')}
        >
          Per-Game
        </button>
        <button
          className={`user-stats-board-tab ${activeTab === 'headtohead' ? 'active' : ''}`}
          onClick={() => setActiveTab('headtohead')}
        >
          Head-to-Head
        </button>
      </div>

      <div className="user-stats-board-content">
        {activeTab === 'overall' && renderOverallStats()}
        {activeTab === 'pergame' && renderGameStats()}
        {activeTab === 'headtohead' && renderHeadToHead()}
      </div>
    </div>
  );
};

export default Scoreboard;
